<template>
  <div :class="{ 'is--full-width-small': props._index === 0 }" class="section-carousel">
    <Carousel
      class="section-carousel-inner"
      gradient="background"
      @active-slide-change="onSlideChange"
    >
      <li
        v-for="(slide, index) in media"
        :key="`slide_${index}`"
        class="slide"
        :style="{
          '--slide-width-percentage': slideWidthPercentages[index],
        }"
        :class="[{ 'is--single-slide': media?.length === 1 }]"
      >
        <component
          :is="getComponent(slide.collection)"
          v-if="slide.collection && slide.item"
          v-bind="{
            staticData: {item: (slide.item as object)},
            ...useImagePriority(_index === 0 ? 'high' : 'auto', 4, index),
          }"
          class="slide-inner"
          :_index="index"
          :confine="media?.length === 1"
          :show-caption="true"
          :padded="media?.length === 1"
          :initial-status="activeSlide === index && autoplay ? 'playing' : 'paused'"
        />
      </li>
    </Carousel>
  </div>
</template>

<script setup lang="ts">
import type {
  ICmsContentCarousels,
  ICmsContentEmbeds,
  ICmsContentImages,
} from '~~/types/_cms-types';
import useImagePriority from '~/composables/useImagePriority';
const { toCamelCase } = formatUtils();

interface IProps extends ICmsContentCarousels {
  _index: number;
  autoplay: boolean;
}

const props = defineProps<IProps>();
const activeSlide = ref(-1);

const getComponent = (collection: string) => {
  return useDynamicComponent(toCamelCase(collection))?.component;
};

// Images carry aspectRatio directly; embeds carry it nested under embed.oembed.
const getItemAspectRatio = (item: ICmsContentEmbeds | ICmsContentImages) => {
  const base = item.aspectRatio || item.embed.oembed.aspectRatio || '16/9';
  return base.split(/[x:]/).join('/');
};

const calculateWidthPercentageFromAspectRatio = (
  item: ICmsContentEmbeds | ICmsContentImages,
): number => {
  const [widthRatio, heightRatio] = getItemAspectRatio(item).split('/').map(Number);
  return widthRatio / heightRatio;
};

// Memoized so autoplay-driven activeSlide changes don't recompute every slide's width.
const slideWidthPercentages = computed(() =>
  props.media?.map((slide) => calculateWidthPercentageFromAspectRatio(slide.item)),
);

const onSlideChange = (index: number) => {
  activeSlide.value = index;
};
</script>

<style lang="postcss" scoped>
.section-carousel-inner {
  --slide-width-percentage: 1;

  --slide-height: calc(100% / 16 * 9);
  @media (--md-viewport) {
    --slide-height: 268px;
  }

  @media (--lg-viewport) {
    --slide-height: 346px;
  }

  :deep(.carousel-wrapper):after {
    height: var(--slide-height);
  }
}

.slide {
  position: relative;
  overflow: hidden;

  width: calc(var(--slide-height) * var(--slide-width-percentage));

  &.is--single-slide {
    width: 100%;
    height: auto;
    cursor: default !important;
  }

  > .slide-inner {
    width: 100%;
    height: auto;
  }

  :deep(iframe) {
    pointer-events: none; /* Disable iframe interaction while dragging */
  }

  .draggable-container.active:deep(iframe) {
    pointer-events: auto; /* Enable iframe interaction when not dragging */
  }

  :deep(picture),
  :deep(.npo-player),
  :deep(.provider),
  :deep(iframe) {
    height: 100%;
  }
}

:deep(.carousel-nav) {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--slide-height);
}
</style>
